// ============================================================
// Cloupak 国际化运行时
//
// - I18nProvider：挂在应用根部，持有当前语言状态
// - useI18n()：组件内获取 { lang, setLang, t }
// - translate(lang, key, params)：纯函数翻译，供非 React 代码使用
// - getLang()：读取模块级当前语言（API 客户端等场景）
//
// 语言优先级：localStorage 保存值 > 浏览器语言（zh 开头 → zh，否则 en）
// 切换语言时同步 <html lang>，并持久化到 localStorage
// ============================================================

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { zh, en, type TranslationKey, type TranslationValue } from './translations';

export type Lang = 'zh' | 'en';

export type TranslateParams = Record<string, string | number>;

export type { TranslationKey };

const LANG_STORAGE_KEY = 'cloupak_lang';

const DICTS: Record<Lang, Record<TranslationKey, TranslationValue>> = { zh, en };

/** 把 {name} 占位符替换为参数值，未命中的占位符原样保留 */
function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match,
  );
}

/** 纯函数翻译：支持 {placeholder} 插值；值为复数对象时按 params.count 选择 */
export function translate(
  lang: Lang,
  key: TranslationKey,
  params?: TranslateParams,
): string {
  const value = DICTS[lang][key] ?? zh[key];
  if (value === undefined) return key;
  const text =
    typeof value === 'string'
      ? value
      : params?.count === 1
        ? value.one
        : value.other;
  return interpolate(text, params);
}

/** 侦测初始语言：localStorage 保存值 > 浏览器语言 */
function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved === 'zh' || saved === 'en') return saved;
  } catch {
    // localStorage 不可用时忽略，走浏览器语言
  }
  return navigator.language?.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

// 模块级当前语言，供 API 客户端等非 React 代码读取；由 I18nProvider 维护
let currentLang: Lang = detectLang();

interface I18nContextValue {
  /** 当前语言 */
  lang: Lang;
  /** 切换语言（持久化 + 更新 <html lang>） */
  setLang: (lang: Lang) => void;
  /** 翻译函数，键有编译期类型检查 */
  t: (key: TranslationKey, params?: TranslateParams) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(currentLang);

  // 同步 <html lang>，利于无障碍与字体选择
  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en';
  }, [lang]);

  function setLang(next: Lang) {
    currentLang = next;
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // 写入失败不影响本次会话内切换
    }
    setLangState(next);
  }

  const t = (key: TranslationKey, params?: TranslateParams) =>
    translate(lang, key, params);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

/** 在组件中获取 i18n 状态与方法，必须在 I18nProvider 内部使用 */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n 必须在 I18nProvider 内部使用');
  }
  return ctx;
}

/** 读取模块级当前语言（供 API 客户端等非 React 代码构造报错文案） */
export function getLang(): Lang {
  return currentLang;
}
